'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import HiringHeader from './components/HiringHeader';
import JobPostings from './components/JobPostings';
import ApplicantPipeline from './components/ApplicantPipeline';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  jobPostingsService,
  applicantsService,
  type JobPosting as SBJobPosting,
  type Applicant as SBApplicant,
  type PipelineStage,
  type JobType,
  type JobStatus,
} from '@/lib/services/staffingService';

export type HiringView = 'jobs' | 'pipeline';
export type { PipelineStage };

export interface JobPosting {
  id: string;
  title: string;
  department: string;
  location: string;
  type: JobType;
  status: JobStatus;
  postedDate: string;
  closingDate: string;
  applicants: number;
  newApplicants: number;
  description: string;
  requirements: string[];
  payRange: string;
}

export interface Applicant {
  id: string;
  name: string;
  role: string;
  jobId: string;
  jobTitle: string;
  email: string;
  phone: string;
  stage: PipelineStage;
  appliedDate: string;
  lastUpdated: string;
  resumeFileName: string | null;
  resumeUrl: string | null;
  notes: string;
  rating: number;
  experience: string;
  location: string;
  tags: string[];
}

// Convert service types to local types
function toLocalJob(j: SBJobPosting, applicants: SBApplicant[]): JobPosting {
  const jobApplicants = applicants.filter((a) => a.jobId === j.id);
  return {
    id: j.id,
    title: j.title,
    department: j.department,
    location: j.location,
    type: j.jobType,
    status: j.status,
    postedDate: j.postedDate,
    closingDate: j.closingDate || '',
    applicants: jobApplicants.length,
    newApplicants: jobApplicants.filter((a) => a.stage === 'applied').length,
    description: j.description,
    requirements: j.requirements,
    payRange: j.payRange,
  };
}

function toLocalApplicant(a: SBApplicant): Applicant {
  return {
    id: a.id,
    name: a.name,
    role: a.role,
    jobId: a.jobId || '',
    jobTitle: a.jobTitle,
    email: a.email,
    phone: a.phone,
    stage: a.stage,
    appliedDate: a.appliedDate,
    lastUpdated: a.lastUpdated,
    resumeFileName: a.resumeFileName,
    resumeUrl: a.resumeUrl,
    notes: a.notes,
    rating: a.rating,
    experience: a.experience,
    location: a.location,
    tags: a.tags,
  };
}

export default function HiringPage() {
  const [view, setView] = useState<HiringView>('jobs');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [jobs, apps] = await Promise.all([
        jobPostingsService.getAll(),
        applicantsService.getAll(),
      ]);
      setApplicants(apps.map(toLocalApplicant));
      setJobPostings(jobs.map((j) => toLocalJob(j, apps)));
    } catch {
      toast.error('Failed to load hiring data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    const unsubJobs = jobPostingsService.subscribe(
      (j) => setJobPostings((prev) => {
        const apps = applicants.map((a) => ({ ...a, jobId: a.jobId || null } as SBApplicant));
        return [toLocalJob(j, apps), ...prev];
      }),
      (j) => setJobPostings((prev) => prev.map((x) => {
        if (x.id !== j.id) return x;
        return { ...x, title: j.title, status: j.status, department: j.department, location: j.location, type: j.jobType, closingDate: j.closingDate || '', description: j.description, requirements: j.requirements, payRange: j.payRange };
      })),
      (id) => setJobPostings((prev) => prev.filter((x) => x.id !== id)),
    );

    const unsubApps = applicantsService.subscribe(
      (a) => setApplicants((prev) => [toLocalApplicant(a), ...prev]),
      (a) => setApplicants((prev) => prev.map((x) => (x.id === a.id ? toLocalApplicant(a) : x))),
      (id) => setApplicants((prev) => prev.filter((x) => x.id !== id)),
    );

    return () => { unsubJobs(); unsubApps(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData]);

  const handleViewPipeline = (jobId: string) => {
    setSelectedJobId(jobId);
    setView('pipeline');
  };

  const handleBackToJobs = () => {
    setSelectedJobId(null);
    setView('jobs');
  };

  const handleAddJob = async (job: JobPosting) => {
    try {
      const created = await jobPostingsService.create({
        title: job.title,
        department: job.department,
        location: job.location,
        jobType: job.type,
        status: job.status,
        postedDate: job.postedDate,
        closingDate: job.closingDate,
        description: job.description,
        requirements: job.requirements,
        payRange: job.payRange,
      });
      if (created) {
        setJobPostings((prev) => [toLocalJob(created, []), ...prev]);
        toast.success('Job posting created');
      }
    } catch {
      toast.error('Failed to create job posting');
    }
  };

  const handleUpdateJob = async (updated: JobPosting) => {
    try {
      await jobPostingsService.update(updated.id, {
        title: updated.title,
        status: updated.status,
        department: updated.department,
        location: updated.location,
        jobType: updated.type,
        closingDate: updated.closingDate,
        description: updated.description,
        requirements: updated.requirements,
        payRange: updated.payRange,
      });
      setJobPostings((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
    } catch {
      toast.error('Failed to update job posting');
    }
  };

  const handleUpdateApplicant = async (updated: Applicant) => {
    try {
      await applicantsService.update(updated.id, {
        stage: updated.stage,
        rating: updated.rating,
        notes: updated.notes,
        resumeFileName: updated.resumeFileName,
        resumeUrl: updated.resumeUrl,
        tags: updated.tags,
        lastUpdated: updated.lastUpdated,
      });
      setApplicants((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch {
      toast.error('Failed to update applicant');
    }
  };

  const handleAddApplicant = async (applicant: Applicant) => {
    try {
      const created = await applicantsService.create({
        name: applicant.name,
        role: applicant.role,
        jobId: applicant.jobId || null,
        jobTitle: applicant.jobTitle,
        email: applicant.email,
        phone: applicant.phone,
        stage: applicant.stage,
        appliedDate: applicant.appliedDate,
        lastUpdated: applicant.lastUpdated,
        resumeFileName: applicant.resumeFileName,
        resumeUrl: applicant.resumeUrl,
        notes: applicant.notes,
        rating: applicant.rating,
        experience: applicant.experience,
        location: applicant.location,
        tags: applicant.tags,
      });
      if (created) {
        setApplicants((prev) => [toLocalApplicant(created), ...prev]);
        setJobPostings((prev) =>
          prev.map((j) =>
            j.id === applicant.jobId
              ? { ...j, applicants: j.applicants + 1, newApplicants: j.newApplicants + 1 }
              : j
          )
        );
        toast.success('Applicant added');
      }
    } catch {
      toast.error('Failed to add applicant');
    }
  };

  const selectedJob = jobPostings.find((j) => j.id === selectedJobId) ?? null;
  const pipelineApplicants = selectedJobId
    ? applicants.filter((a) => a.jobId === selectedJobId)
    : applicants;

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground text-sm">Loading hiring data…</span>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-5 fade-in">
        <HiringHeader
          view={view}
          selectedJob={selectedJob}
          onBack={handleBackToJobs}
          totalJobs={jobPostings.filter((j) => j.status === 'active').length}
          totalApplicants={applicants.length}
          newApplicants={jobPostings.reduce((s, j) => s + j.newApplicants, 0)}
        />
        {view === 'jobs' ? (
          <JobPostings
            jobs={jobPostings}
            applicants={applicants}
            onViewPipeline={handleViewPipeline}
            onAddJob={handleAddJob}
            onUpdateJob={handleUpdateJob}
          />
        ) : (
          <ApplicantPipeline
            job={selectedJob}
            applicants={pipelineApplicants}
            onUpdateApplicant={handleUpdateApplicant}
            onAddApplicant={handleAddApplicant}
            onBack={handleBackToJobs}
          />
        )}
      </div>
    </AppLayout>
  );
}
